import { designVariables } from "@studio3t/design-variables";
import {
  ButtonSize,
  ButtonSmallSecondaryIcon,
  GenericButton,
  Icon,
} from "@studio3t/generic-components";
import type React from "react";
import { useCallback, useId } from "react";
import styled from "styled-components";
import { DEFAULT_INGEST_PORT } from "@/constants";
import { validateRouteFormat } from "./routeValidation";
import {
  FieldRow,
  FieldRowControl,
  FormSection,
  HelpText,
  Input,
  Label,
  ListAddRow,
  SectionTitle,
} from "./styles";

const FORM = designVariables.ui.form;
const INPUT = designVariables.ui.input;

interface HttpSourceFieldsProps {
  routes: string[];
  setRoutes: (routes: string[]) => void;
}

// Routes render as an editable list of input rows (Figma 14057-47211): every
// row past the first can be removed with its minus button (a lone row has
// none), the last row is the one being typed and carries the format hint,
// and Add appends a row.
export function HttpSourceFields({ routes, setRoutes }: HttpSourceFieldsProps) {
  const portInputId = useId();
  const routeInputId = useId();

  const lastIndex = routes.length - 1;

  const updateRoute = useCallback(
    (index: number, value: string) => {
      setRoutes(routes.map((r, i) => (i === index ? value : r)));
    },
    [routes, setRoutes],
  );

  // Removing the only row resets the list to its empty state: one blank
  // row to type into.
  const removeRoute = useCallback(
    (index: number) => {
      const rest = routes.filter((_, i) => i !== index);
      setRoutes(rest.length > 0 ? rest : [""]);
    },
    [routes, setRoutes],
  );

  const addRow = useCallback(() => {
    setRoutes([...routes, ""]);
  }, [routes, setRoutes]);

  // Gates Enter-to-add only; the Add button stays enabled, since submit
  // drops blank rows.
  const rowComplete =
    routes[lastIndex].trim() !== "" && !routeError(routes, lastIndex);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (rowComplete) addRow();
      }
    },
    [rowComplete, addRow],
  );

  return (
    <FormSection>
      <SectionTitle>HTTP Source configuration</SectionTitle>

      {/* The shared port isn't editable; the disabled input look tells that
          apart from the regular fields (Figma 14057-46793 disabled state). */}
      <FieldRow>
        <Label htmlFor={portInputId}>Ingest port</Label>
        <FieldRowControl>
          <Input
            id={portInputId}
            type="text"
            value={DEFAULT_INGEST_PORT}
            disabled
          />
          <HelpText>
            All HTTP Source services share a single ingest server on this port.
          </HelpText>
        </FieldRowControl>
      </FieldRow>

      <RoutesList>
        <Label htmlFor={`${routeInputId}-0`}>Routes</Label>
        {routes.map((value, index) => (
          <RouteRowItem
            // Rows are positional (values live in state), so the index key
            // is stable enough here.
            // biome-ignore lint/suspicious/noArrayIndexKey: see above
            key={index}
            value={value}
            index={index}
            isLast={index === lastIndex}
            error={routeError(routes, index)}
            inputId={`${routeInputId}-${index}`}
            removable={routes.length > 1}
            onChange={updateRoute}
            onKeyDown={handleKeyDown}
            onRemove={removeRoute}
          />
        ))}

        <ListAddRow>
          <GenericButton
            // GenericButton's `type` prop is the variant; the HTML type
            // attribute is set via ref.
            ref={(el) => el?.setAttribute("type", "button")}
            type="secondary"
            size={ButtonSize.Small}
            icon={Icon.Plus}
            iconPosition="leading"
            text="Add"
            onClick={addRow}
          />
        </ListAddRow>
      </RoutesList>
    </FormSection>
  );
}

// Live per-row verdict; toPayload re-checks the same rules on submit.
function routeError(routes: string[], index: number): string | null {
  const value = routes[index].trim();
  if (!value) return null;
  const formatError = validateRouteFormat(value);
  if (formatError) return formatError;
  if (routes.slice(0, index).some((r) => r.trim() === value))
    return `Duplicate route '${value}'`;
  return null;
}

// One route row: the input, its minus button (hidden while the row is the
// only one), and the supportive line under it (format hint on the row
// being typed, the error when the row has one).
function RouteRowItem({
  value,
  index,
  isLast,
  error,
  inputId,
  removable,
  onChange,
  onKeyDown,
  onRemove,
}: {
  value: string;
  index: number;
  isLast: boolean;
  error: string | null;
  inputId: string;
  removable: boolean;
  onChange: (index: number, value: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onRemove: (index: number) => void;
}) {
  return (
    <RouteRowGroup>
      <RouteRow>
        <Input
          id={inputId}
          type="text"
          value={value}
          onChange={(e) => onChange(index, e.target.value)}
          onKeyDown={isLast ? onKeyDown : undefined}
          placeholder={isLast ? "Orders/Created" : undefined}
        />
        {removable && (
          <ButtonSmallSecondaryIcon
            // GenericButton derivatives drop type/aria props; both are
            // set on the real <button> via ref (type defaults to
            // "submit" inside the form).
            ref={(el) => {
              el?.setAttribute("type", "button");
              el?.setAttribute(
                "aria-label",
                `Remove route ${value || index + 1}`,
              );
            }}
            icon={Icon.Minus}
            onClick={() => onRemove(index)}
          />
        )}
      </RouteRow>
      {error ? (
        <RouteError>{error}</RouteError>
      ) : (
        <HelpText>
          {isLast
            ? "Alphanumeric, hyphens, underscores, and slashes. No leading/trailing slash, no double slashes."
            : // Zero-width placeholder keeps every row the same height
              // (and reserves the line its error appears on).
              "​"}
        </HelpText>
      )}
    </RouteRowGroup>
  );
}

// Label + rows + Add button stack. Row spacing comes from each row's
// supportive line, matching the Figma stack (14057-47460, gap 0).
const RoutesList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  > ${Label} {
    margin-bottom: ${INPUT.gaplabel}px;
  }
`;

const RouteRowGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${INPUT.gapsupportive}px;
`;

const RouteRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${FORM.gapitems}px;

  > ${Input} {
    flex: 1;
    min-width: 0;
  }
`;

const RouteError = styled.span`
  font-size: 11px;
  color: #dc2626;
`;
